import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Typography, Paper, Chip, CircularProgress } from "@mui/material";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import TaskCard from "./TaskCard";
import DropZone from "./DropZone";
import InsertionIndicator from "./InsertionIndicator";

const ITEMS_PER_PAGE = 10;

const KanbanColumn = ({ column, tasks, title, color, dragOverlay }) => {
  const [displayedTasks, setDisplayedTasks] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef();
  const loadMoreRef = useRef(null);

  const { setNodeRef, isOver } = useDroppable({
    id: column,
  });

  useEffect(() => {
    // Reset when tasks change
    const initialTasks = tasks.slice(0, ITEMS_PER_PAGE);
    setDisplayedTasks(initialTasks);
    setPage(1);
    setHasMore(tasks.length > ITEMS_PER_PAGE);
  }, [tasks]);

  const loadMoreTasks = useCallback(() => {
    if (loading || !hasMore) return;

    setLoading(true);
    setTimeout(() => {
      const nextPage = page + 1;
      const startIndex = (nextPage - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      const newTasks = tasks.slice(startIndex, endIndex);

      if (newTasks.length > 0) {
        setDisplayedTasks((prev) => [...prev, ...newTasks]);
        setPage(nextPage);
        setHasMore(endIndex < tasks.length);
      } else {
        setHasMore(false);
      }
      setLoading(false);
    }, 300); // Simulate network delay
  }, [page, tasks, loading, hasMore]);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "20px",
      threshold: 0.1,
    };

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadMoreTasks();
      }
    }, options);

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadMoreTasks]);

  const getColumnStyles = () => {
    const baseStyles = {
      backgroundColor: "#f8f9fa",
      borderRadius: 2,
      p: 2,
      minHeight: "600px",
      maxHeight: "80vh",
      minWidth: "280px",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      border: "2px solid transparent",
      position: "relative",
      overflow: "hidden",
    };

    if (isOver) {
      return {
        ...baseStyles,
        backgroundColor: `${color}08`,
        borderColor: color,
        borderStyle: "solid",
        borderWidth: "3px",
        boxShadow: `0 8px 32px ${color}30, 0 0 0 1px ${color}20, inset 0 0 0 1px ${color}15`,
        transform: "translateY(-2px) scale(1.02)",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${color}10 0%, ${color}05 50%, ${color}10 100%)`,
          borderRadius: "inherit",
          zIndex: -1,
        },
      };
    }

    return baseStyles;
  };

  return (
    <Paper elevation={0} sx={getColumnStyles()}>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" fontWeight="bold" sx={{ color: color }}>
          {title}
        </Typography>
        <Chip
          label={tasks.length}
          size="small"
          sx={{
            backgroundColor: `${color}20`,
            color: color,
            fontWeight: "bold",
          }}
        />
      </Box>

      <Box
        ref={setNodeRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          pr: 1,
          position: "relative",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f1f1",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#888",
            borderRadius: "4px",
            "&:hover": {
              backgroundColor: "#555",
            },
          },
        }}
      >
        <SortableContext
          items={displayedTasks.map((task) => task.id)}
          strategy={verticalListSortingStrategy}
        >
          {displayedTasks.map((task, index) => {
            const isDraggedOver = dragOverlay && 
              dragOverlay.overId && 
              dragOverlay.overId === task.id &&
              dragOverlay.activeId !== task.id;
            
            return (
              <React.Fragment key={task.id}>
                {isDraggedOver && (
                  <InsertionIndicator color={color} isVisible={true} />
                )}
                <TaskCard task={task} />
              </React.Fragment>
            );
          })}
          
          {/* Show insertion indicator at the end if dragging over column or dropzone */}
          {dragOverlay && dragOverlay.activeId && (
            (dragOverlay.overId === column || 
             dragOverlay.overId === `dropzone-${column}` ||
             (dragOverlay.overId === column && !displayedTasks.find(t => t.id === dragOverlay.overId))
            ) && (
              <InsertionIndicator color={color} isVisible={true} />
            )
          )}
        </SortableContext>

        {hasMore && (
          <Box
            ref={loadMoreRef}
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 2,
              minHeight: "50px",
            }}
          >
            {loading && <CircularProgress size={24} />}
          </Box>
        )}

        {!hasMore &&
          displayedTasks.length > 0 &&
          displayedTasks.length < tasks.length && (
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ py: 1 }}
            >
              Showing {displayedTasks.length} of {tasks.length} tasks
            </Typography>
          )}

        <DropZone column={column} color={color} isEmpty={tasks.length === 0} />
      </Box>
    </Paper>
  );
};

export default KanbanColumn;
